import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(!isHome);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return;
    }
    setScrolled(window.scrollY > 60);
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const textColor = scrolled ? 'text-foreground-900' : 'text-background-50';
  const mutedColor = scrolled ? 'text-foreground-600' : 'text-background-50/80';
  const hoverColor = scrolled ? 'hover:text-primary-600' : 'hover:text-background-100';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2">
          <span className={`font-heading text-xl font-bold tracking-tight transition-colors duration-300 ${textColor}`}>
            Basetao<span className={scrolled ? 'text-accent-600' : 'text-accent-400'}>Sheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${textColor} ${hoverColor}`}>
            <i className="ri-home-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            Home
          </Link>
          <a href="#what-is" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${mutedColor} ${hoverColor}`}>
            What Is
          </a>
          <a href="#why-choose" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${mutedColor} ${hoverColor}`}>
            Features
          </a>
          <a href="#comparison" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${mutedColor} ${hoverColor}`}>
            Compare
          </a>
          <a href="#categories" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${mutedColor} ${hoverColor}`}>
            Categories
          </a>
          <a href="#guides" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${mutedColor} ${hoverColor}`}>
            Guides
          </a>
          <Link
            to="/article/basetao-review"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${mutedColor} ${hoverColor}`}
          >
            Shopping Guide
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Visit Official Site
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${textColor}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-1">
          <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm font-semibold text-foreground-800 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Home
          </Link>
          <div className="border-t border-background-200/50 my-1"></div>
          <a href="#what-is" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">What Is Basetao</a>
          <a href="#why-choose" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">Features</a>
          <a href="#comparison" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">Compare Tools</a>
          <a href="#categories" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">Categories</a>
          <a href="#guides" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">Guides</a>
          <Link to="/article/basetao-review" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
            Shopping Guide
          </Link>
          <div className="border-t border-background-200/50 my-1"></div>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            onClick={() => setMobileOpen(false)}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer"
          >
            Visit Official Site
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>
      )}
    </nav>
  );
}